import React, {
  useState,
  useEffect,
  createContext,
  useContext,
} from 'react';

import * as jwt from 'jsonwebtoken';
import { LoginData } from '../pages/Login/types';
import {
  getToken,
  removeUser,
  setUser,
  getUser,
} from '../services/user';
import { login } from '../services/user/requests';
import api from '../services/api';
import { User } from '../services/user';

interface AuthContextProps {
  authenticated: boolean;
  loading: boolean;
  login: (form: LoginData) => Promise<boolean>;
  logout: () => void;
}

interface DecodedToken {
  email: string;
  sub: string;
  exp: number;
  id: string;
  company: string;
  iss: string;
  iat: number;
  full_name: string;
  receiver_hash?: string;
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@Quero2Seller:token');
    const user = localStorage.getItem('@Quero2Seller:user');

    if (token && user) {
      api.defaults.headers.authorization = `Bearer ${token}`;

      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const signIn = async (form: LoginData) => {
    setLoading(true);
    try {
      const response = await login(form);
      
      // Se a resposta tem uma mensagem de erro
      if (response.message) {
        console.error('Erro de login:', response.message);
        return false;
      }

      // Se a resposta tem o token
      if (!response.token) {
        console.error('Token não encontrado na resposta');
        return false;
      }

      const loginToken = response.token;
      const decoded = jwt.decode(loginToken) as DecodedToken || null;

      if (!decoded) {
        console.error('Token inválido ou não decodificável');
        return false;
      }

      console.log('Token JWT decodificado:', decoded);

      setUser({
        token: loginToken,
        userId: decoded.id,
        name: decoded.full_name,
        email: decoded.email,
        receiverHash: decoded.receiver_hash
      });
      setToken(loginToken);
    } catch (error) {
      console.error('Erro no login:', error);
      return false;
    }
    setLoading(false);
    return true;
  };

  const signOut = () => {
    removeUser();
    setToken(null);
  };

  useEffect(() => {
    if (getToken()) { // Prevent old connections
      setLoading(false);
      return;
    }
    setToken(getToken());
    setLoading(false);
    if (getToken() && getUser()) {
      const newUser = getUser();
      if (newUser && newUser.token) {
        delete newUser.token;
        setUser({
          ...newUser,
        });
      }
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authenticated: !!token,
        loading,
        login: signIn,
        logout: signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  return context;
};
